import { LS_ROLES, LS_SELECTED_SHOP_IDS } from 'constants/index';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPermission, setSelectedShopIds } from 'store/reducers/shop';

export const useSelectedShopId = () => {
    const dispatch = useDispatch();

    const user = useSelector((state) => state.shop.user);
    const permissions = useSelector((state) => state.shop.permission || []);
    const roles = useSelector((state) => state.role);
    const shopList = useSelector((state) => state.shop.list);
    const stateSelectedShopIds = useSelector((state) => state.shop.selectedShopIds);

    const selectedShopId = useMemo(() => {
        const shopId = stateSelectedShopIds[0];

        if (typeof shopId === 'undefined') {
            return null;
        }

        return +shopId;
    }, [stateSelectedShopIds]);

    const selectedShopIds = useMemo(() => stateSelectedShopIds.map((shopId) => +shopId), [stateSelectedShopIds]);

    const selectedShop = useMemo(() => {
        const shopId = stateSelectedShopIds[0];

        if (typeof shopId === 'undefined') {
            return null;
        }

        return shopList.find((shop) => shop.id === +shopId) ?? null;
    }, [shopList, stateSelectedShopIds]);

    const selectedShops = useMemo(() => {
        const shopMap = new Map(shopList.map((shop) => [shop.id, shop]));
        return stateSelectedShopIds.map((shopId) => shopMap.get(+shopId)).filter(Boolean);
    }, [shopList, stateSelectedShopIds]);

    const currency = useMemo(
        () =>
            selectedShop?.currency || {
                decimal_num: 0,
                code: 'Rp',
                decimal: ',',
                thousand: '.'
            },
        [selectedShop?.currency]
    );

    const reservationCurrency = useMemo(
        () =>
            selectedShop?.reservationCurrency || {
                decimal_num: 0,
                code: 'Rp',
                decimal: ',',
                thousand: '.'
            },
        [selectedShop?.reservationCurrency]
    );

    const handleChangeShopIds = useCallback(
        (shopIds) => {
            if (!Array.isArray(shopIds)) {
                dispatch(setSelectedShopIds([shopIds]));
                localStorage.setItem(LS_SELECTED_SHOP_IDS, JSON.stringify([shopIds]));

                return;
            }

            dispatch(setSelectedShopIds(shopIds));
            localStorage.setItem(LS_SELECTED_SHOP_IDS, JSON.stringify(shopIds));
        },
        [dispatch]
    );

    useEffect(() => {
        if (selectedShops.length <= 0) {
            return;
        }

        window.Intercom('update', {
            name: selectedShops[0]?.name ?? '',
            shop_id: selectedShops[0]?.id ?? -1
        });

        const roles = JSON.parse(localStorage.getItem(LS_ROLES));
        const permission = roles.find((role) => role.shop_id === selectedShops[0].id)?.dashboard_permissions || [];
        dispatch(setPermission(permission));
    }, [dispatch, selectedShops]);

    return {
        permissions,
        roles,
        shopList,
        user,

        selectedShopId,
        selectedShopIds,
        selectedShop,
        selectedShops,

        currency,
        reservationCurrency,

        handleChangeShopIds
    };
};
